<template>
  <!--
    `relative z-30` is load-bearing, not decoration: the glass surface gives this
    header a `backdrop-filter`, which makes it a stacking context — and pages
    teleport their own menus INTO the header (the dashboard actions menu, the
    display-params sheet). Without a z-index here those menus would be trapped
    under the page content behind them; with it they float over the page while
    still sitting below the drawers (z-40/50) and modals (z-60+).
  -->
  <header
    class="relative z-30 pt-safe glass-surface bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm transition-colors duration-200">
    <div class="px-4">
      <!--
        ONE row at every width. The same controls used to be split over TWO rows
        on phones (identity + title + status on top; navigation + page actions +
        display options + account below), which is what the owner's earlier
        "should have new row for the buttons" asked for and what his later report
        reverses: the wrap reads as a broken bar, and at 390px it costs a whole
        line of height for controls that fit beside each other.

        The single row is CSS, not two containers: every portal below is a single
        DOM node pages teleport into, so moving one between wrappers would mean
        duplicating chrome per page. The page title is the only flexible item and
        the only one that gives way — the global rule on #page-header-portal in
        style.css truncates it — so the icon buttons always keep their width.
        Height follows the content (a 3.5rem row plus the safe-area padding), so
        the bar can never clip its own buttons the way a fixed `h-14` box would.

        The page's own screens strip (#page-tabs-portal — the dashboard switcher,
        teleported in by the page) is the one exception, and only for the pages
        that carry one: with it on the page the row wraps below `md`, so the strip
        docks on its own full-width line under the title row, and from `md` up the
        title stops taking the leftover width and the strip takes it — the bar
        keeps the title on the left, the status and the page's own buttons on the
        right, and the screens CENTERED between them. Both rules are keyed on the
        strip actually being there (`:has`), so every other page keeps the single
        no-wrap line it has always had.
      -->
      <div class="top-bar-row flex flex-nowrap items-center justify-between gap-1 min-h-14">
        <!-- Mobile menu button (hidden on desktop) -->
        <button
          @click="ui.toggleMobileSidebar()"
          class="flex items-center justify-center w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-600 bg-cover bg-center flex-shrink-0 mr-2 hover:opacity-90 transition-opacity md:hidden"
          :style="
            systemStore.imageUrl
              ? `background-image: url('${systemStore.imageUrl}')`
              : `background-image: url('${brand.images.avatar}')`
          "
          :title="tt('Menu', 'Meni')"></button>

        <!-- Panel/back controls lead the bar: they open the left rails (rooms,
             settings) and step back, so they sit beside the leading edge —
             before the page's own title, not after the status light. -->
        <div id="page-navigation-portal" class="flex items-center gap-3 flex-shrink-0 min-w-0 min-h-12 empty:hidden">
          <!-- In-app back: phones only, and only below a section root. On a
               section root the leading slot stays free — the drawer owns the
               way out of the hub, the hardware key never does. -->
          <button
            v-if="showMobileBack"
            class="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-gray-600 transition-colors hover:bg-black/5 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white flex-shrink-0"
            :title="mobileBackLabel"
            @click="goBackOneLevel()">
            <span class="material-symbols-outlined text-xl">arrow_back</span>
          </button>

          <button
            v-if="showWidgetBack"
            class="flex items-center justify-center w-10 h-10 rounded-lg text-gray-600 transition-colors hover:bg-black/5 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
            :title="widgetBackLabel"
            @click="goBackFromWidget()">
            <span class="material-symbols-outlined text-xl">arrow_back</span>
          </button>

          <!-- Settings sidebar toggle (all screen sizes) -->
          <button
            v-if="isSettingsActive"
            class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors"
            :class="
              settingsPanelOpen
                ? 'tint-chip'
                : 'text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10'
            "
            :title="tt('Toggle settings menu', 'Preklopi meni nastavitev')"
            @click="toggleSettingsSidebar()">
            <span class="material-symbols-outlined text-xl">{{ settingsPanelOpen ? 'menu_open' : 'menu' }}</span>
          </button>
          <!-- Rooms sidebar toggle (all screen sizes) -->
          <button
            v-if="isRoomsActive"
            class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors"
            :class="
              roomsPanelOpen ? 'tint-chip' : 'text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10'
            "
            :title="tt('Toggle rooms menu', 'Preklopi meni prostorov')"
            @click="toggleRoomsSidebar()">
            <span class="material-symbols-outlined text-xl">{{ roomsPanelOpen ? 'menu_open' : 'menu' }}</span>
          </button>
        </div>

        <!-- The page's own title/icon (from portal) — the item that gives way -->
        <div id="page-header-portal" class="flex items-center gap-3 px-2 flex-1 min-w-0 min-h-14"></div>

        <!-- A page's own extra header control (from portal) — the BIM view's
             storey selector (plan 2026-10-18). A sibling slot, not a child of the
             title portal, so its order after the title is explicit; `empty:hidden`
             keeps the bar exactly as it was on every other page. -->
        <div id="page-header-extra" class="flex items-center gap-2 min-w-0 empty:hidden"></div>

        <!-- The page's own screens strip (from portal — dashboard pages only).
             Phones: a full-bleed line of its own under the title row. The `basis`
             wider than the row is what breaks the line; the matching negative left
             margin (and the `-mt-1`, which eats the row's own gap) is what makes it
             full-bleed and flush — and it is safe to write `px-4` here because this
             row is the one that sits inside that padding. ≥md: inline in the row,
             taking the width the title stops taking; the strip centers itself in
             that box (`md:mx-auto`), which is what puts the tabs in the middle of
             the bar. -->
        <div
          id="page-tabs-portal"
          class="order-last -ml-4 -mt-1 basis-[calc(100%_+_2rem)] min-w-0 shrink-0 empty:hidden md:order-none md:ml-0 md:mt-0 md:basis-auto md:flex-1"></div>

        <!-- The house at a glance: clock, weather, security, heating and the
             shade quick controls, in a right-hand drawer. -->
        <div class="flex items-center flex-shrink-0 min-h-14">
          <button
            @click="ui.toggleSiteDrawer()"
            class="flex items-center justify-center w-10 h-10 rounded-md transition-colors"
            :class="
              ui.siteDrawerOpen
                ? 'bg-sky-100 text-sky-600 dark:bg-sky-900/40 dark:text-sky-400'
                : 'text-gray-600 hover:bg-black/5 dark:text-gray-300 dark:hover:bg-white/10'
            "
            data-testid="site-drawer-toggle"
            :title="tt('At a glance', 'Na pogled')">
            <span class="material-symbols-outlined text-xl">view_sidebar</span>
          </button>
        </div>

        <!-- Connection / system status. It is the "is this hub healthy" light,
             which is why it belongs beside the hub identity rather than at the
             end of the run of buttons. -->
        <div class="flex items-center flex-shrink-0 min-h-14">
          <button
            @click="ui.toggleStatusDrawer()"
            class="relative flex items-center justify-center w-10 h-10 mr-2 rounded-md transition-colors"
            :class="
              ui.statusDrawerOpen
                ? 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400'
                : systemStatusStore.highestSeverity === 'error'
                  ? 'text-red-500'
                  : systemStatusStore.highestSeverity === 'warning'
                    ? 'text-orange-500'
                    : systemStatusStore.highestSeverity === 'alert'
                      ? 'text-blue-500'
                      : 'text-green-500 hover:bg-black/5 dark:hover:bg-white/10'
            "
            :title="tt('System Status', 'Stanje sistema')">
            <span
              class="material-symbols-outlined text-xl"
              :class="systemStatusStore.highestSeverity === 'error' ? 'animate-pulse' : ''"
              >{{
                systemStatusStore.highestSeverity === 'error'
                  ? 'error'
                  : systemStatusStore.highestSeverity === 'warning'
                    ? 'warning'
                    : systemStatusStore.highestSeverity === 'alert'
                      ? 'info'
                      : 'check_circle'
              }}</span
            >
            <span
              v-if="systemStatusStore.totalCount > 0"
              class="absolute top-1 right-1 min-w-[14px] h-3.5 px-0.5 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none"
              :class="
                systemStatusStore.highestSeverity === 'error'
                  ? 'bg-red-500'
                  : systemStatusStore.highestSeverity === 'warning'
                    ? 'bg-orange-500'
                    : 'bg-blue-500'
              "
              >{{ systemStatusStore.totalCount > 9 ? '9+' : systemStatusStore.totalCount }}</span
            >
          </button>
        </div>

        <!-- Page actions portal (e.g. more_vert button) -->
        <div id="page-actions-portal" class="flex items-center flex-shrink-0 min-h-12 empty:hidden"></div>

        <!-- Display params: right-aligned, desktop only -->
        <div id="page-display-params-desktop" class="hidden md:flex items-center gap-3 ml-auto overflow-hidden"></div>

        <!-- Display options (the page's own params) + the account menu -->
        <div class="flex items-center gap-0.5 flex-shrink-0 min-h-12">
          <!-- Mobile menu: the page's own display params. The opener is painted only
               while #page-display-params-mobile actually has something in it — see
               mobileDisplayParamsPresent below. -->
          <div class="md:hidden relative">
            <button
              v-if="mobileDisplayParamsPresent"
              @click="mobileDropdownOpen = !mobileDropdownOpen"
              class="flex items-center justify-center w-10 h-10 rounded-md transition-colors"
              :class="
                mobileDropdownOpen
                  ? 'bg-gray-100 dark:bg-gray-700'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10'
              "
              :title="tt('Display options', 'Možnosti prikaza')">
              <span class="material-symbols-outlined">more_vert</span>
            </button>

            <!-- Mobile dropdown menu. v-show, never v-if, and the host below stays in the
                 DOM: DisplayParams resolves its target once, at mount (a querySelector plus
                 one rAF retry), so a host that only exists while the dropdown is open would
                 never receive anything. Keeping it also lets the opener's condition read
                 the host itself instead of guessing. -->
            <div
              v-show="mobileDropdownOpen"
              class="glass-surface absolute right-0 mt-1 w-56 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 p-3">
              <!-- Display params inserted here from desktop version -->
              <div id="page-display-params-mobile" class="flex flex-col gap-2 mb-2"></div>

              <!-- No edit-mode toggle here: a page's edit mode belongs to the page.
                   DashboardNew teleports its own toggle into #page-actions-portal.
                   The injects this file used to read could never resolve — the layout
                   renders <TopNavigation /> as a sibling of its <slot>, so a page's
                   provide() is out of reach and the label could never flip. -->
            </div>
          </div>
        </div>

        <!-- Guest chip (card t_803e86a4): who is looking at this house while
             it is not theirs. Inside the one row, first to give way. -->
        <DemoGuestPill />

        <!-- Account -->
        <button
          @click="ui.openUserDrawer('user')"
          class="relative flex items-center justify-center w-9 h-9 rounded-md transition-colors"
          :class="ui.userDrawerOpen ? 'ring-2 ring-blue-400 rounded-full' : 'hover:bg-black/5 dark:hover:bg-white/10'"
          :title="tt('Account', 'Račun')">
          <div
            class="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 bg-cover bg-center flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
            :style="avatarUrl && !avatarError ? `background-image: url('${avatarUrl}')` : ''">
            <img v-if="avatarUrl && !avatarError" :src="avatarUrl" class="hidden" @error="avatarError = true" />
            <span v-if="!avatarUrl || avatarError" class="select-none">{{ userInitials }}</span>
          </div>
          <span
            v-if="totalBadgeCount > 0"
            class="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none"
            >{{ totalBadgeCount > 99 ? '99+' : totalBadgeCount }}</span
          >
        </button>
      </div>
    </div>
    <!-- Guest hint strip (card t_803e86a4): one line under the bar, so the
         speed of the demo day is never a mystery. Dismissible. -->
    <DemoHintStrip />
  </header>
</template>

<script setup lang="ts">
import { usePeopleStore } from '../stores/people';
import { useUIStore } from '../stores/ui';
import { brand } from '../config/brand';
import { useSystemStore } from '../stores/system';
import { useSystemStatusStore } from '../stores/systemStatus';
import { useAuthStore } from '../stores/auth';
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useSettingsNav } from '../composables/useSettingsNav';
import { useRoomsNav } from '../composables/useRoomsNav';
import { useWidgetBackNavigation } from '../composables/useWidgetBackNavigation';
import { useMobileBack } from '../composables/useMobileBack';
import { useUserAvatar } from '../composables/useUserAvatar';
import DemoGuestPill from './demo/DemoGuestPill.vue';
import DemoHintStrip from './demo/DemoHintStrip.vue';
import { tt } from '../utils/consoleText';
import { MOBILE_DISPLAY_PARAMS_HOST_ID, displayParamsHostHasContent } from '../utils/displayParamsHost';

const {
  isSettingsActive,
  sidebarOpen: settingsSidebarOpen,
  desktopSidebarOpen: settingsDesktopSidebarOpen,
  isDockedColumn: settingsIsDockedColumn,
  toggleSidebar: toggleSettingsSidebar,
} = useSettingsNav();
/** Which of the two settings surfaces the toggle reflects: the docked column, or the drawer. */
const settingsPanelOpen = computed(() =>
  settingsIsDockedColumn.value ? settingsDesktopSidebarOpen.value : settingsSidebarOpen.value,
);

const {
  isRoomsActive,
  sidebarOpen: roomsSidebarOpen,
  desktopSidebarOpen: roomsDesktopSidebarOpen,
  isDockedColumn: roomsIsDockedColumn,
  toggleSidebar: toggleRoomsSidebar,
} = useRoomsNav();
const roomsPanelOpen = computed(() =>
  roomsIsDockedColumn.value ? roomsDesktopSidebarOpen.value : roomsSidebarOpen.value,
);

const peopleStore = usePeopleStore();
const ui = useUIStore();
const systemStore = useSystemStore();
const systemStatusStore = useSystemStatusStore();
const { showWidgetBack, widgetBackLabel, goBackFromWidget } = useWidgetBackNavigation();
/** The phone header's own back arrow: below a section root only. */
const { showMobileBack, mobileBackLabel, goBackOneLevel } = useMobileBack();
const route = useRoute();
const isCloudLandingPage = computed(
  () =>
    route.path === '/cloud' ||
    route.path === '/home' ||
    route.path === '/servers' ||
    (!!route.meta.cloudView && !route.params.hubId),
);

onMounted(() => {
  if (isCloudLandingPage.value) {
    return;
  }
  systemStatusStore.initialize();
});
const authStore = useAuthStore();
const mobileDropdownOpen = ref(false);

/**
 * Whether the shell's mobile display-params host currently holds something.
 *
 * The dropdown exists only to carry the page's own controls (search fields, view
 * switches, floor-plan zoom), and a page is free to teleport nothing at all —
 * dashboard pages do exactly that: DashboardNew renders no DisplayParams, and its
 * own `⋯` already sits in the same bar via #page-actions-portal, so an opener that
 * is always painted opens an empty panel on the app's home route.
 *
 * The condition has to be read off the host's DOM and kept live (pages teleport
 * asynchronously, and in-app navigation swaps the page under a shell that never
 * remounts), so a mount-time count is wrong twice over. `displayParamsHostHasContent`
 * says what counts: element children or non-blank text — not the empty text anchors
 * Vue's Teleport leaves behind for a page that renders `<DisplayParams></DisplayParams>`
 * with nothing in it (Settings, People, Intercom, Account).
 */
const mobileDisplayParamsPresent = ref(false);
let displayParamsObserver: MutationObserver | null = null;

onMounted(() => {
  const host = document.getElementById(MOBILE_DISPLAY_PARAMS_HOST_ID);
  if (!host) return;
  const sync = () => {
    mobileDisplayParamsPresent.value = displayParamsHostHasContent(host);
  };
  displayParamsObserver = new MutationObserver(sync);
  displayParamsObserver.observe(host, { childList: true, subtree: true, characterData: true });
  sync();
});

onBeforeUnmount(() => {
  displayParamsObserver?.disconnect();
  displayParamsObserver = null;
});

// With the host emptied, the opener goes with it — so nothing may stay open behind it.
watch(mobileDisplayParamsPresent, (present) => {
  if (!present) mobileDropdownOpen.value = false;
});

// Hub avatar resolved through the routed API layer (works in local, cloud WebRTC
// and Electron), with a fallback to the cloud account image in cloud mode.
const { url: avatarUrl, error: avatarError } = useUserAvatar();

const unreadChats = computed(() => peopleStore.unreadTotal);
const totalBadgeCount = computed(() => unreadChats.value);

const userInitials = computed(() => {
  if (authStore.user?.name) {
    return authStore.user.name
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }
  return authStore.user?.email?.substring(0, 2).toUpperCase() || 'U';
});
</script>

<style scoped>
/* Phones: the screens strip is a line of its own under the title row — the bar
   wraps exactly while that strip is on the page. (`:has()` cannot nest another
   `:has()` — the inner check is spelled as the child selector it means.) */
@media (max-width: 767px) {
  .top-bar-row:has(#page-tabs-portal > *) {
    flex-wrap: wrap;
  }
}

/* ≥md: the strip joins the row, so the title stops taking the leftover width and
   the strip takes it — and centers the tabs in that box, which is the middle of
   the bar. `flex: 0 1 auto` keeps the title shrinkable (a long title still
   truncates instead of overflowing the bar). */
@media (min-width: 768px) {
  .top-bar-row:has(#page-tabs-portal > *) > #page-header-portal {
    flex: 0 1 auto;
  }
}
</style>
